import { z } from 'zod';

// Spam/discriminatory keywords to block
const blockedKeywords = [
  'solo italiani',
  'max 30 anni',
  'bella presenza',
  'disponibile nero',
  'in nero',
  'senza contratto',
  'guadagna facile',
  'lavoro da casa garantito',
  'reddito passivo',
  'mlm',
  'network marketing',
];

// Check if text contains contact info
const containsContactInfo = (text: string): boolean => {
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  const phoneRegex = /(\+39)?[\s.-]?\d{2,4}[\s.-]?\d{6,8}/;
  return emailRegex.test(text) || phoneRegex.test(text);
};

// Check for blocked keywords
const hasBlockedKeywords = (text: string): boolean => {
  const lowerText = text.toLowerCase();
  return blockedKeywords.some(keyword => lowerText.includes(keyword));
};

export const jobPostingSchema = z.object({
  title: z.string()
    .min(10, 'jobs.validation.titleTooShort')
    .max(200, 'jobs.validation.titleTooLong')
    .refine(val => !hasBlockedKeywords(val), 'jobs.validation.blockedKeywords'),
  
  description: z.string()
    .min(200, 'jobs.validation.descriptionTooShort')
    .max(10000, 'jobs.validation.descriptionTooLong')
    .refine(val => !containsContactInfo(val), 'jobs.validation.noContactInDescription')
    .refine(val => !hasBlockedKeywords(val), 'jobs.validation.blockedKeywords'),
  
  contract_type: z.enum(['determinato', 'indeterminato', 'partita_iva', 'stage', 'apprendistato']),
  
  salary_range_min: z.number().min(0).optional().nullable(),
  salary_range_max: z.number().optional().nullable(),
  salary_period: z.enum(['annuale', 'mensile', 'orario']).default('annuale'),
  
  work_mode: z.enum(['presenza', 'remoto', 'ibrido']),
  location_city: z.string().min(2, 'jobs.validation.cityRequired').max(100),
  location_region: z.string().max(100).optional().nullable(),
  
  required_skills: z.array(z.string()).min(3, 'jobs.validation.minSkills'),
  nice_to_have_skills: z.array(z.string()).default([]),
  inclusive_benefits: z.array(z.string()).min(2, 'jobs.validation.minBenefits'),
  inclusivity_tags: z.array(z.string()).min(1, 'jobs.validation.minTags').max(5, 'jobs.validation.maxTags'),
  
  application_method: z.enum(['external_link', 'email', 'upload_cv']),
  application_url: z.string().url().startsWith('https://').optional().nullable().or(z.literal('')),
  application_email: z.string().email().optional().nullable().or(z.literal('')),
}).refine(
  data => {
    if (data.salary_range_min && data.salary_range_max) {
      return data.salary_range_max > data.salary_range_min;
    }
    return true;
  },
  { message: 'jobs.validation.salaryRange', path: ['salary_range_max'] }
).refine(
  data => {
    if (data.application_method === 'external_link') {
      return !!data.application_url;
    }
    return true;
  },
  { message: 'jobs.validation.urlRequired', path: ['application_url'] }
).refine(
  data => {
    if (data.application_method === 'email') {
      return !!data.application_email;
    }
    return true;
  },
  { message: 'jobs.validation.emailRequired', path: ['application_email'] }
);

export const jobApplicationSchema = z.object({
  applicant_name: z.string()
    .min(2, 'jobs.validation.nameTooShort')
    .max(255, 'jobs.validation.nameTooLong'),
  applicant_email: z.string()
    .email('jobs.validation.invalidEmail')
    .max(255),
  applicant_phone: z.string().max(50).optional().nullable().or(z.literal('')),
  cover_letter: z.string().max(1000, 'jobs.validation.coverLetterTooLong').optional().nullable().or(z.literal('')),
});

export type JobPostingFormData = z.infer<typeof jobPostingSchema>;
export type JobApplicationFormData = z.infer<typeof jobApplicationSchema>;
